import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  LucideAngularModule,
  Eye,
  EyeOff,
  RefreshCw,
  AlertCircle,
  User,
  Phone,
  Shield,
  KeyRound,
} from 'lucide-angular';
import { AuthService } from '../../../../core/services/auth.service';
import { EmployeeService } from '../../../../core/services/employee.service';
import { UserProfile } from '../../../../core/models/auth.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-settings-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, TranslatePipe],
  templateUrl: './profile.component.html',
})
export class SettingsProfileComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly employeeService = inject(EmployeeService);
  private readonly destroyRef$ = inject(DestroyRef);

  Eye = Eye;
  EyeOff = EyeOff;
  RefreshCw = RefreshCw;
  AlertCircle = AlertCircle;
  User = User;
  Phone = Phone;
  Shield = Shield;
  KeyRound = KeyRound;

  profile = signal<UserProfile | null>(null);
  isLoading = signal(false);
  errorMsg = signal<string | null>(null);

  // Avatar upload state
  avatarUploading = signal(false);

  // PIN (pinCode) state
  pinVisible = signal(false);
  isPinRegenerating = signal(false);
  pinError = signal<string | null>(null);

  ngOnInit() {
    const cached = this.authService.user();
    if (cached) {
      this.profile.set({
        id: cached.id,
        fullName: cached.fullName,
        phoneNumber: cached.phoneNumber,
        gender: cached.gender,
        role: cached.role ?? '',
        avatar: cached.avatar,
        isSystemUser: cached.isSystemUser,
      });
    }
    this.fetchProfile();
  }

  fetchProfile() {
    this.isLoading.set(true);
    this.authService
      .getMyProfile()
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: (res) => {
          if (res.success) this.profile.set(res.data);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
  }

  get isEmployee(): boolean {
    return this.profile()?.role?.toLowerCase() === 'employee';
  }

  /**
   * POS (kassa) ilovasiga PIN bilan kira oladigan foydalanuvchilar:
   * Employee (xodim) va Customer (do'kon egasi/owner).
   */
  get isPinUser(): boolean {
    const role = this.profile()?.role?.toLowerCase();
    return role === 'employee' || role === 'customer';
  }

  get initials(): string {
    const name = this.profile()?.fullName ?? '';
    return name
      .split(' ')
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('');
  }

  get roleLabel(): string {
    const role = this.profile()?.role ?? '';
    const map: Record<string, string> = {
      admin: 'Administrator',
      systemuser: 'Tizim foydalanuvchisi',
      employee: 'Xodim',
      customer: "Do'kon egasi",
    };
    return map[role.toLowerCase()] ?? role;
  }

  onAvatarSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) return;
    if (file.size > 5 * 1024 * 1024) return;

    this.avatarUploading.set(true);
    this.authService
      .uploadAvatar(file)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: (res) => {
          if (res.success && res.data?.avatarUrl) {
            this.profile.update((p) => (p ? { ...p, avatar: res.data.avatarUrl } : p));
          }
          this.avatarUploading.set(false);
        },
        error: () => this.avatarUploading.set(false),
      });
    input.value = '';
  }

  removeAvatar() {
    this.avatarUploading.set(true);
    this.authService
      .deleteAvatar()
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: () => {
          this.profile.update((p) => (p ? { ...p, avatar: undefined } : p));
          this.avatarUploading.set(false);
        },
        error: () => this.avatarUploading.set(false),
      });
  }

  togglePinVisibility() {
    this.pinVisible.update((v) => !v);
  }

  regeneratePin() {
    const id = this.profile()?.id;
    if (!id) return;
    this.isPinRegenerating.set(true);
    this.pinError.set(null);
    this.employeeService
      .regeneratePin(id)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: (res) => {
          const newPin = res.data?.pinCode ?? res.data?.pin;
          if (res.success && newPin) {
            this.profile.update((p) => (p ? { ...p, pinCode: newPin } : p));
            this.pinVisible.set(true);
          }
          this.isPinRegenerating.set(false);
        },
        error: (err) => {
          this.pinError.set(err?.error?.message ?? 'PIN yangilashda xatolik');
          this.isPinRegenerating.set(false);
        },
      });
  }
}
